import { useState, useEffect, useRef } from 'react';
import { getToken } from '../api/client.js';

const API_BASE = import.meta.env.VITE_API_URL || '/api';
const cache = new Map();

export function useFontBinary(fontId, fileId) {
  const [font, setFont] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!fontId || !fileId) { setFont(null); return; }

    const cacheKey = `${fontId}-${fileId}`;
    if (cache.has(cacheKey)) {
      setFont(cache.get(cacheKey));
      return;
    }

    setLoading(true);
    setError(null);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      try {
        const url = `${API_BASE}/fonts/${fontId}/serve/${fileId}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${getToken()}` },
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const buffer = await res.arrayBuffer();

        // Dynamic import opentype.js for tree shaking
        const opentype = await import('opentype.js');
        const parsed = opentype.parse(buffer);
        cache.set(cacheKey, parsed);
        setFont(parsed);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [fontId, fileId]);

  return { font, loading, error };
}
